import { clamp, hash01, hash3, mixRgb, placeKey, smoothstep } from '@/core/rng';
import { CHUNK_SIZE, type ChunkData, type Instance } from './chunk';
import { chunkHeightAt } from './env';
import type { ThingStoreSnapshot } from '@/sim/persistence/snapshot';

/**
 * Promotion: the sparse store of things history has made significant
 * (Expansion II §4, `ROADMAP.md` §11).
 *
 * §4 says *persistence follows significance* — ordinary tree → important
 * tree. Until now a god could channel a verb into one particular tree and
 * the world would record it in the event log and nowhere else: the tree
 * greened, the env deviation decayed on an eighteen-day half-life, and a
 * blessed tree was an ordinary tree again inside a season. The act was
 * narrated, not kept.
 *
 * This is the layer that keeps it. It is deliberately the smallest thing
 * that can be true:
 *
 * - **Identity is already free.** Baseline content is a pure function of
 *   position and seed, so a tree's position *is* its name. The key here is
 *   `placeKey` — the same string `ui/attention.ts` puts in a target's id and
 *   `sim.act` writes into the event as `targetId`. No ids are minted, no
 *   entities are allocated, and nothing is promoted for being *looked* at.
 * - **CURRENT WORLD = BASELINE + SPARSE HISTORY.** An empty store costs
 *   exactly nothing: `applyThings` returns the chunk untouched, byte for
 *   byte. Only divergence is stored.
 * - **Significance is earned.** One cast does not consecrate anything.
 *   Standing accumulates across acts and decays on a *twenty-five-year*
 *   half-life — three orders of magnitude slower than the env layer beneath
 *   it, which is the whole point. Repetition sustains it indefinitely;
 *   total neglect eventually returns the tree to being a tree, which is §4
 *   read in the other direction.
 *
 * What it is not: this store holds only things whose identity *is* their
 * place. People, bands, structures, paths and anchors already carry ids and
 * already persist; promoting them is a different container (§49). Bare
 * ground is excluded too — pointing at open ground is what the picker
 * returns when you are pointing at nothing, and a god who wants bare ground
 * to matter has `anchors` for exactly that.
 */

/** What can be promoted: baseline things, the ones whose place is their name. */
export type ThingKind = 'plant' | 'rock';

/**
 * "Is the thing at this place one a condition may not remove?"
 *
 * The three transforms beneath promotion all cull: `applyEnv` kills the
 * understory under a hard Wither, `applyWear` clears a camp, `applyOverlay`
 * burns small plants away. Promotion runs *last* and only ever recolours, so
 * on its own it can never bring back a plant one of them already took — and
 * the sacred tree in the middle of the village would hold its mark and render
 * nothing at all. This is the one thread promotion has to pass back down the
 * chain, and it is deliberately the narrowest possible one: a yes/no about a
 * place, with no idea what a verb, a god or a standing is.
 */
export type Spare = (x: number, z: number) => boolean;

/**
 * The predicate to hand the other three transforms, or `undefined` where
 * nothing has ever been promoted — so an untouched world does not even
 * allocate a closure, let alone ask a question per plant.
 */
export const sparing = (things: ThingStore, now: number): Spare | undefined =>
  things.size === 0 ? undefined : (x, z) => things.isImportant(x, z, now);

/** How much of the standing ladder one act at full concentration buys. */
export const ACT_GAIN = 0.25;

/**
 * Standing at or above this is *important*: the world renders it and the
 * chronicle can name it. Three fully-channelled acts clear it; a valley-wide
 * cast that merely happens to name a tree takes about twenty.
 */
export const PROMOTE = 0.55;

/** Standing half-life, world-seconds. Twenty-five years — history's tempo. */
export const STANDING_HALF = 25 * 365 * 86400;

/** Below this a standing is noise and the thing is forgotten entirely. */
export const NEGLIGIBLE = 0.02;

/**
 * Metres of ground that carry a promoted thing's mark. Wider than the thing
 * itself on purpose: a tree crown is a couple of metres and would be a
 * sub-pixel dot from the altitude the game is played at, where the ground is
 * what actually reads.
 */
export const MARK_GROUND = 9;

/** One promoted thing. `standing` is signed as of `at`; age is derived. */
export interface ThingMark {
  /** `placeKey` — identity, and the store's key. */
  key: string;
  kind: ThingKind;
  /** The thing's own base, world metres. */
  x: number; z: number;
  /**
   * Signed significance: positive where a god tended it, negative where one
   * blasted it. Both are significance — a lightning-split oak is as much a
   * thing history keeps as a blessed one. Magnitude is what promotes.
   */
  standing: number;
  /** World time `standing` was last written; decay runs from here. */
  at: number;
  /** World time it first crossed into importance. History, so it is kept. */
  since: number;
  /** How many times a god meant this particular thing. */
  acts: number;
}

const chunkOf = (x: number, z: number) =>
  `${Math.floor(x / CHUNK_SIZE)},${Math.floor(z / CHUNK_SIZE)}`;

const decay = (v: number, dt: number) => v * Math.pow(0.5, dt / STANDING_HALF);

/** A mark decayed to `now`, or null once it has fallen to nothing. */
function aged(m: ThingMark, now: number): ThingMark | null {
  const standing = decay(m.standing, now - m.at);
  if (Math.abs(standing) < NEGLIGIBLE) return null;
  return { ...m, standing, at: now };
}

/**
 * WHEN THIS MARK STOPS BEING A PROMOTION — analytically, not by polling.
 *
 * Standing decays as `s0 · 2^(−Δt / STANDING_HALF)`, so the instant it crosses
 * `PROMOTE` is exact and closed-form. A sweep that watched for the crossing
 * would have to run often enough not to miss it and would give a different
 * answer at different step sizes; this gives the same world-second whether it
 * is asked once a century or every day, which is law 7's whole demand.
 *
 * `Infinity` for a mark that never was a promotion — there is nothing to lapse.
 */
export const lapsesAt = (m: ThingMark): number => {
  const s0 = Math.abs(m.standing);
  if (s0 < PROMOTE) return Infinity;
  return m.at + STANDING_HALF * (Math.log(s0 / PROMOTE) / Math.LN2);
};

export class ThingStore {
  private readonly marks = new Map<string, ThingMark>();
  private version_ = 0;

  /**
   * Record that a god meant *this* thing.
   *
   * `weight` is 0..1 — how pointedly it was meant, which the caller derives
   * from the cast's concentration; the world does not know what a verb is.
   * `sign` is +1 for tending and -1 for blasting. Existing standing decays
   * to `at` first and then accumulates, so repetition deepens what a single
   * act only starts — the same shape as `EnvField.stamp`, three orders of
   * magnitude slower.
   *
   * Returns the chunk keys whose appearance changed, for `invalidate()`.
   */
  /**
   * Returns the chunks to redraw, and — new — whether this act is the one that
   * CROSSED the threshold.
   *
   * `wasImportant` was already computed here and thrown away, so the moment a
   * thing stopped being ordinary happened in total silence: no event, no line,
   * nothing for a witness to carry home. The only evidence a player ever had
   * was a recolour they had to already be looking at. Repetition is the
   * mechanic this whole store exists to express, and nothing said so.
   */
  mark(
    kind: ThingKind,
    x: number, z: number,
    weight: number, sign: 1 | -1,
    at: number,
  ): { dirty: string[]; crossed: boolean; standing: number } {
    const gain = clamp(weight, 0, 1) * ACT_GAIN;
    if (gain <= 0) return { dirty: [], crossed: false, standing: 0 };
    const key = placeKey(x, z);
    const prev = this.marks.get(key);
    const carried = prev ? decay(prev.standing, at - prev.at) : 0;
    const standing = clamp(carried + gain * sign, -1, 1);
    const wasImportant = Math.abs(carried) >= PROMOTE;
    this.marks.set(key, {
      key, kind, x, z, standing, at,
      // `since` is the moment it *became* important and never moves again
      // while it stays so — a thing that lapses and is re-consecrated dates
      // from the second consecration, which is what a history would say.
      since: wasImportant && prev ? prev.since : at,
      acts: (prev?.acts ?? 0) + 1,
    });
    this.version_++;
    return {
      dirty: [chunkOf(x, z)],
      // the act that took it over, and only that one: a fourth cast on an
      // already-important tree deepens it and announces nothing
      crossed: !wasImportant && Math.abs(standing) >= PROMOTE,
      standing,
    };
  }

  /** The mark at a place, decayed to now. Null where nothing was ever meant. */
  at(x: number, z: number, now: number): ThingMark | null {
    const m = this.marks.get(placeKey(x, z));
    return m ? aged(m, now) : null;
  }

  /** Whether the thing at a place has been made important. */
  isImportant(x: number, z: number, now: number): boolean {
    const m = this.at(x, z, now);
    return m !== null && Math.abs(m.standing) >= PROMOTE;
  }

  /**
   * Every *important* mark standing in one chunk, decayed to now, in the
   * store's own insertion order — which is replay order, and survives a
   * snapshot. Marks below the promotion threshold are remembered but not
   * rendered: they are a history of attention, not yet a sacred thing.
   */
  inChunk(cx: number, cz: number, now: number): ThingMark[] {
    const want = `${cx},${cz}`;
    const out: ThingMark[] = [];
    for (const m of this.marks.values()) {
      if (chunkOf(m.x, m.z) !== want) continue;
      const a = aged(m, now);
      if (a && Math.abs(a.standing) >= PROMOTE) out.push(a);
    }
    return out;
  }

  /** Everything currently important, anywhere — for the chronicle and probes. */
  important(now: number): ThingMark[] {
    const out: ThingMark[] = [];
    for (const m of this.marks.values()) {
      const a = aged(m, now);
      if (a && Math.abs(a.standing) >= PROMOTE) out.push(a);
    }
    return out;
  }

  /** Forget things that have decayed to nothing; return their chunk keys. */
  sweep(now: number): string[] {
    const dirty = new Set<string>();
    for (const [key, m] of this.marks) {
      if (aged(m, now) === null) {
        this.marks.delete(key);
        dirty.add(chunkOf(m.x, m.z));
      }
    }
    if (dirty.size > 0) this.version_++;
    return [...dirty];
  }

  /** All chunk keys currently carrying a mark. */
  chunks(): string[] {
    const keys = new Set<string>();
    for (const m of this.marks.values()) keys.add(chunkOf(m.x, m.z));
    return [...keys];
  }

  /**
   * Every place whose promotion ran out inside `(from, to]`.
   *
   * The one caller is the daily sweep, which hands these to the Chronicle so a
   * god stops being paid for a thing the world has forgotten (`WIRING.md` A13).
   * A half-open span keyed on the analytic crossing means each lapse is
   * reported exactly once however the advance was sliced — the property every
   * absolute-grid system in this build has to earn.
   *
   * Pure: a read of the marks and some arithmetic. Sorted, because the
   * Chronicle's own order must not depend on `Map` iteration.
   */
  lapsedBetween(from: number, to: number): string[] {
    const out: string[] = [];
    for (const [key, m] of this.marks) {
      const at = lapsesAt(m);
      if (at > from && at <= to) out.push(key);
    }
    return out.sort();
  }

  get size(): number { return this.marks.size; }

  /** A cache tag, not history — the same contract as `EnvField.version`. */
  get version(): number { return this.version_; }

  /** Every mark, copied out in insertion order (spec §49). */
  toSnapshot(): ThingStoreSnapshot {
    return { marks: [...this.marks.values()].map((m) => ({ ...m })) };
  }

  /** Rebuild a store from a snapshot. Shares nothing with the source. */
  static fromSnapshot(snap: ThingStoreSnapshot): ThingStore {
    const store = new ThingStore();
    for (const m of snap.marks) store.marks.set(m.key, { ...m });
    return store;
  }
}

// A tended thing deepens and warms — the green of a tree that is doing well
// in a way the rest of the hillside is not. A blasted one goes toward bone,
// which is deliberately *not* the burn overlay's char-black: a struck tree
// and a burnt one must not read alike, or the god's mark is just fire.
const TENDED = [0.16, 0.40, 0.14] as const;
const TENDED_LIT = [0.62, 0.72, 0.30] as const;
const BLASTED = [0.74, 0.72, 0.66] as const;
// and the ground under a BLASTED thing: pale exhausted earth. The tended
// ground's paint (`TENDED_EARTH`, a darker richer earth) was RETIRED on
// 2026-08-22 by ruling (`DECISIONS.md`, the First Playable lane's rulings):
// §11 photographed it as a hard-edged coloured slab — a 9 m radius on a 4 m
// vertex grid can only be sampled at r = 0, 4, 8 — and `growThings` is the
// right carrier for significance (ROADMAP §43.4, a ×1.408 crown and a grove
// measured at 300 m). A struck tree has no growth to show, so its ground
// keeps the one mark that says *this one was struck* from play altitude.
// `MARK_GROUND` and this colour are untouched: the constants were never the
// bug, and PARKING-LOT.md's do-not-tune order still stands over them.
const BLASTED_EARTH = [0.66, 0.63, 0.57] as const;

/**
 * How strongly a mark shows, 0..1. Never zero at the threshold: a thing that
 * has just become important looks important, and deepens from there.
 */
const showing = (standing: number): number =>
  0.35 + 0.65 * clamp((Math.abs(standing) - PROMOTE) / (1 - PROMOTE), 0, 1);

/**
 * Stamp promotion onto freshly built chunk data, on the main thread — the
 * fourth transform, after `applyEnv`, `applyWear` and `applyOverlay`.
 *
 * Last on purpose. The three layers beneath it are all *conditions* — how
 * wet the ground is, how hard it has been walked, what burned — and they all
 * decay. Promotion is not a condition, it is a status, and a sacred tree
 * standing in a burn should read as a sacred tree in a burn rather than
 * disappear under the char. So this runs over the top of them.
 *
 * It moves nothing and removes nothing. It recolours, and — since 2026-08-15 —
 * it GROWS: a tended tree outgrows its ordinary self and a grove comes in
 * around it (`growthOf`, `groveOf` below). Law 8 is kept the way `bloomCover`
 * keeps it: the same mark lands on the same plant identically at every LOD,
 * the grove is drawn from one fixed candidate list per mark and coarse rungs
 * take an even stride of it — they omit, they never invent — and both terms
 * read QUANTISED world time so rungs of one cell built minutes apart agree to
 * the byte (`GROW_Q`).
 *
 * Mutates in place and returns the same object.
 */
export function applyThings(data: ChunkData, things: ThingStore, now: number): ChunkData {
  if (things.size === 0) return data;
  const marks = things.inChunk(data.cx, data.cz, now);
  // The growth pass reads its marks decayed to the QUANTUM, not the instant
  // (`inChunk` rewrites `at`, so this is the only place the quantum can be
  // applied), and from the 3×3 neighbourhood, because the grove of a tree in
  // the next chunk can spill into this one. The paint and the recolour keep to
  // this chunk's own marks at `now`, exactly as before.
  const nowQ = Math.floor(now / GROW_Q) * GROW_Q;
  const grown: ThingMark[] = [];
  for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
    for (const m of things.inChunk(data.cx + dx, data.cz + dz, nowQ)) grown.push(m);
  }
  if (marks.length === 0 && grown.length === 0) return data;
  if (marks.length === 0) return growThings(data, grown, nowQ);

  // ---- the ground a BLASTED thing keeps ----
  // Only the struck: a tended thing shows as growth (`growThings`), never as
  // paint, by ruling. Blended by strongest contribution rather than in
  // sequence, so two marks whose ground overlaps give the same result
  // whatever order they are held in. Nothing about how the world is rendered
  // may depend on replay order.
  const blasted = marks.filter((m) => m.standing < 0);
  if (blasted.length > 0) {
    const step = CHUNK_SIZE / (data.res - 1);
    const ox = data.cx * CHUNK_SIZE, oz = data.cz * CHUNK_SIZE;
    for (let j = 0; j < data.res; j++) for (let i = 0; i < data.res; i++) {
      const x = ox + i * step, z = oz + j * step;
      let best = 0, from = '';
      for (const m of blasted) {
        const fall = 1 - smoothstep(0.25, 1, Math.hypot(m.x - x, m.z - z) / MARK_GROUND);
        if (fall <= 0) continue;
        const w = fall * showing(m.standing);
        // strictly stronger wins; an exact tie — two symmetric marks either
        // side of a vertex — is settled by name, so it is not settled by the
        // order the world happened to be replayed in
        if (w > best || (w === best && from !== '' && m.key < from)) {
          best = w; from = m.key;
        }
      }
      if (best <= 0) continue;
      const k = (j * data.res + i) * 3;
      const c = mixRgb(
        [data.colour[k], data.colour[k + 1], data.colour[k + 2]],
        BLASTED_EARTH, best * 0.45,
      );
      data.colour[k] = c[0]; data.colour[k + 1] = c[1]; data.colour[k + 2] = c[2];
    }
  }

  // ---- and the thing itself ----
  // Exact identity: a plant's place *is* its name, so this is a key lookup,
  // not a search. A mark whose plant is not in this chunk's list — culled by
  // wear, burned to nothing by the overlay, or simply omitted at a coarse
  // LOD — colours no plant this rebuild and is not lost: the mark is on the
  // place, and when the plant is there again it is important again.
  const byKey = new Map(marks.map((m) => [m.key, m]));
  for (const inst of data.vegetation) {
    const m = byKey.get(placeKey(inst.x, inst.z));
    if (!m) continue;
    const w = showing(m.standing);
    const c = inst.colour;
    if (m.standing > 0) {
      const deep = mixRgb([c[0], c[1], c[2]], TENDED, w * 0.55);
      const lit = mixRgb(deep, TENDED_LIT, w * 0.30);
      inst.colour = [lit[0], lit[1], lit[2]];
    } else {
      const bone = mixRgb([c[0], c[1], c[2]], BLASTED, w * 0.72);
      inst.colour = [bone[0], bone[1], bone[2]];
    }
  }

  return growThings(data, grown, nowQ);
}

/*
 * SIGNIFICANCE SHOWS AS GROWTH (2026-08-15).
 *
 * `ROADMAP.md` §11 photographed promotion and it failed: the ground mark
 * read as a coloured slab and the tree was a dark speck inside it. Griffin's
 * direction — the ground was the wrong carrier from the start; a verb
 * channelled into a tree should hit THAT TREE hardest and radiate to its
 * neighbours, more plants around it, bushes coming into berry — so that
 * significance shows as living things doing something rather than as tinted
 * dirt. The paint above is left exactly as it was (its constants are parked
 * on purpose; whether it stays is a merge decision). This is the mechanism.
 *
 * Two terms, both read from the mark and nothing else (law 2 — nothing new
 * is stored, a grove is derived from the standing it grows out of):
 *
 * - **The tree grows.** A tended tree's `scaleXZ` and `scaleY` are multiplied
 *   by `1 + GROWTH · showing · ripen(age)`: 12% the day it is promoted, +50%
 *   a decade on. Law 29 — the act is immediate, the legacy takes time. Blasted
 *   trees keep the recolour only: a lightning-split oak is important, not
 *   large. Measured before choosing the number: at play altitude (2.1 px/m
 *   from 400 m) a 12 m crown is ~25 px, so +50% is 38 px — the biggest thing
 *   in its clearing — and at 3 km (0.28 px/m) none of this reads, which is
 *   the same far-view gap §11 already lists for env and wear.
 * - **A grove comes in.** Up to `GROVE_MAX` shrubs and tufts around each
 *   tended tree, positions hashed from the mark's own key (law 1: a place is
 *   its name, so a grove is the same grove on every machine), thinning with
 *   distance to `GROVE_R`, the count rising with standing and ripening over
 *   two world-years, bushes coming into berry above `BERRY_AT` — the same
 *   0.62 `bloomCover` uses. Coarse rungs take every `stride`-th candidate,
 *   as `bloomCover` does, so LOD 1 draws a subset of LOD 0 and never a bush
 *   LOD 0 does not have. Each chunk appends only the instances inside its own
 *   bounds and reads marks from the 3×3 neighbourhood, so a grove on a chunk
 *   edge is seamless. It is appended after wear and the overlay have culled,
 *   so it is spared as its tree is (§11: the camp builds around it, the fire
 *   goes round it).
 *
 * WHY QUANTISED TIME. `applyThings` is handed `now` per build, and the rungs
 * of one cell are built at different moments — a cached LOD 0 remounts beside
 * a fresh LOD 1. §34's finding was a ladder that drew a different surface at
 * every rung, so both terms read `nowQ = floor(now / GROW_Q) · GROW_Q` and a
 * standing decayed to that instant rather than to `now`: every rung built in
 * the same quantum computes the same size and the same grove, to the byte.
 * There is NO cadence at which every rung of a cell is guaranteed rebuilt (a
 * cached view lives until evicted or invalidated), so the pop at a quantum
 * boundary is bounded instead of avoided: with `GROW_Q` a world-month, growth
 * moves at most `GROWTH · 0.75 / 60` = 0.6% of a crown per boundary at full
 * standing (0.2 px of a 38 px crown at 300 m) and the grove gains at most one
 * bush (`GROVE_MAX` over 24 months). A view left in cache across a boundary
 * remounts at the older quantum's size until it is next rebuilt.
 */
/** Quantum of world time the growth terms read: thirty world-days. */
export const GROW_Q = 30 * 86400;
/** How much a fully tended, fully ripened tree outgrows itself, per dimension. */
export const GROWTH = 0.5;
/** World-years for a tended tree to reach its full growth. */
const GROWTH_YEARS = 5;
/** Metres a tended tree's grove reaches, and how many things it holds at full standing. */
export const GROVE_R = 22;
export const GROVE_MAX = 28;
/** World-years for the grove to fill in. */
const GROVE_YEARS = 2;
/** Grove bushes come into berry above this standing-derived vigour — bloomCover's own bar. */
const BERRY_AT = 0.62;
const YEAR_S = 365 * 86400;
const GROVE_SALT = 0x6e0e;
const GROVE_SHRUB = [0.24, 0.35, 0.16] as const;
const GROVE_GRASS = [0.30, 0.44, 0.17] as const;
const GROVE_GOLD = [0.57, 0.51, 0.25] as const;
const GROVE_BERRY = [[0.42, 0.10, 0.12], [0.20, 0.13, 0.30]] as const;

/**
 * A mark as the growth terms read it: decayed to the QUANTUM. `ThingStore.at`
 * and `inChunk` rewrite `at` to the instant they were asked for, so the
 * caller passes marks it fetched at `nowQ` (as `applyThings` does) — or, from
 * a store, `store.at(x, z, nowQ)`; a mark fetched at any other instant is
 * re-decayed here, which is exact when `nowQ ≥ m.at` and the same tiny
 * (≤ 0.23%, one month against a 25-year half-life) overshoot `inChunk(nowQ)`
 * itself gives for a mark written later in the quantum otherwise.
 */
const standingAt = (m: ThingMark, nowQ: number): number =>
  m.at === nowQ ? m.standing : decay(m.standing, nowQ - m.at);

/** 1 + how much a tended tree has grown by `nowQ`; 1 for anything else. */
export function growthOf(m: ThingMark, nowQ: number): number {
  const s = standingAt(m, nowQ);
  if (s <= 0 || s < PROMOTE) return 1;
  const age = Math.max(0, nowQ - m.since);
  const ripen = 0.25 + 0.75 * smoothstep(0, GROWTH_YEARS * YEAR_S, age);
  return 1 + GROWTH * showing(s) * ripen;
}

/**
 * The grove a tended tree carries as of `nowQ`: how many of its candidates
 * are alive, and the vigour they wear. The caller takes `count` of them at
 * its stride. Positions, sizes and tones are hashed from the mark's key and
 * index — never from time — so the ones that exist are always the same ones.
 */
export function groveOf(m: ThingMark, nowQ: number): { count: number; vigour: number } {
  const s = standingAt(m, nowQ);
  if (s <= 0 || s < PROMOTE) return { count: 0, vigour: 0 };
  const age = Math.max(0, nowQ - m.since);
  const vigour = showing(s);
  const count = Math.round(GROVE_MAX * vigour * smoothstep(0, GROVE_YEARS * YEAR_S, age));
  return { count, vigour };
}

/** The k-th grove candidate of a mark: where, how big, what tone. Pure. */
function groveCandidate(m: ThingMark, k: number, vigour: number) {
  const seed = hash3(Math.round(m.x * 4), Math.round(m.z * 4), GROVE_SALT);
  const h = (salt: number) => hash01(seed, k, salt);
  // denser near the trunk, thinning to the edge; never on the trunk itself
  const r = GROVE_R * (0.18 + 0.82 * Math.sqrt(h(1)));
  const a = h(2) * Math.PI * 2;
  const shrub = h(3) < 0.35 + vigour * 0.3;
  const grow = h(4), tone = h(5), jitter = h(6), berry = h(7);
  const hgt = shrub ? (0.9 + grow * 0.55) * (1 + vigour * 0.85)
                    : (0.45 + grow * 0.65) * (1 + vigour * 0.35);
  let col = mixRgb(shrub ? GROVE_SHRUB : GROVE_GRASS, GROVE_GOLD, tone * (shrub ? 0.25 : 0.7));
  col = mixRgb(col, TENDED, vigour * 0.35);
  if (shrub && vigour > BERRY_AT && berry < 0.34)
    col = mixRgb(col, GROVE_BERRY[hash3(k, Math.round(m.x), 0xbe22) % 2], 0.28 + (vigour - BERRY_AT) * 0.5);
  const shade = 0.88 + jitter * 0.28;
  return {
    x: m.x + Math.cos(a) * r, z: m.z + Math.sin(a) * r,
    scaleXZ: hgt * (shrub ? 1.4 + tone * 0.6 : 0.9 + tone * 0.5),
    scaleY: hgt * (0.85 + grow * 0.30),
    rot: h(8) * Math.PI * 2,
    colour: [col[0] * shade, col[1] * shade, col[2] * shade] as [number, number, number],
    kind: (shrub ? 'shrub' : 'tuft') as Instance['kind'],
  };
}

/**
 * The growth pass: the tended trees of this chunk grow, and the groves of
 * this chunk's trees and its neighbours' come in, clipped to this chunk.
 * `grown` is every important mark of the 3×3 neighbourhood, decayed to `nowQ`.
 */
function growThings(data: ChunkData, grown: ThingMark[], nowQ: number): ChunkData {
  // the tree itself — only a mark standing in THIS chunk can name a plant here
  const x0 = data.cx * CHUNK_SIZE, z0 = data.cz * CHUNK_SIZE;
  const inside = (x: number, z: number) =>
    x >= x0 && x < x0 + CHUNK_SIZE && z >= z0 && z < z0 + CHUNK_SIZE;
  const own = grown.filter((m) => inside(m.x, m.z));
  if (own.length > 0) {
    const byKey = new Map(own.map((m) => [m.key, m]));
    for (const inst of data.vegetation) {
      const m = byKey.get(placeKey(inst.x, inst.z));
      if (!m) continue;
      const g = growthOf(m, nowQ);
      if (g === 1) continue;
      inst.scaleXZ *= g;
      inst.scaleY *= g;
    }
  }
  // the grove
  if (data.lod > 2) return data;
  const stride = data.lod === 0 ? 1 : data.lod === 1 ? 2 : 4;
  const added: Instance[] = [];
  for (const m of grown) {
    const { count, vigour } = groveOf(m, nowQ);
    for (let k = 0; k < count; k++) {
      if (k % stride !== 0) continue;
      const c = groveCandidate(m, k, vigour);
      if (!inside(c.x, c.z)) continue;
      const y = chunkHeightAt(data, c.x, c.z);
      if (y <= 1.0) continue;             // the shore: no grove in the sea
      added.push({ x: c.x, y, z: c.z, scaleXZ: c.scaleXZ, scaleY: c.scaleY, rot: c.rot,
                   colour: c.colour, kind: c.kind, variant: 0 });
    }
  }
  if (added.length > 0) data.vegetation = data.vegetation.concat(added);
  return data;
}
